import { ResearchResult } from "./client";

export function useRefreshResultIds() {
    return useState<Set<number>>('hard-refresh-ids', () => new Set());
}
export function useResearchResultsCache() {
    return useState<{ [key: number]: ResearchResult }>('research-results', () => ({}));
}
export function useResearchResult(fn: () => number | undefined) {
    const resultsCache = useResearchResultsCache();
    return computed(() => {
        const id = fn();
        if (!id) return null;
        return id in resultsCache.value ? resultsCache.value[id] : null;
    })
}
function useResearchResultRefCount() {
    const state = useState<{ [key: number]: number }>('research-result-reference-count', () => ({}))
    const inc = (id: number) => {
        if (!(id in state.value)) state.value[id] = 0;
        state.value[id]++;
    }
    const dec = (id: number) => {
        if (!(id in state.value)) {
            state.value[id] = 0;
            return
        }
        state.value[id]--;
    }
    return [inc, dec, state] as const;
}

export function useResearchResultRefs(fn: () => number[]) {
    const [inc, dec] = useResearchResultRefCount();
    watch(fn, (newVal, oldVal) => {
        const addition = newVal.filter(id => !(id in (oldVal ?? []))) ?? [];
        const deletion = oldVal?.filter(id => !(id in (newVal ?? []))) ?? [];

        for (const id of addition) inc(id);
        for (const id of deletion) dec(id);
    }, { immediate: true })
}

export function useResearchResultRef(fn: () => number | undefined) {
    const [inc, dec] = useResearchResultRefCount();
    watch(fn, (newVal, oldVal) => {
        if (oldVal) dec(oldVal);
        if (newVal) inc(newVal);
    }, { immediate: true });
    onBeforeUnmount(() => {
        const val = fn();
        if (val) dec(val);
    });
}

export function useWatchResearchResult(
    resultIdFn: () => number | undefined,
    inViewFn: () => boolean = () => true) {
    const [inc, dec] = useResearchResultRefCount();
    const refreshResultIds = useRefreshResultIds();
    const researchResults = useResearchResultsCache();

    const researchResult = computed(() => {
        const resultId = resultIdFn();
        if (!resultId) return null;
        return resultId in researchResults.value ?
            researchResults.value[resultId] : null;
    })

    watch(inViewFn, async (inView) => {
        const resultId = resultIdFn();
        if (!resultId) return;
        if (!inView) {
            dec(resultId);
        } else {
            inc(resultId);
        }
    })
    watch(resultIdFn, (newVal, oldVal) => {
        if (oldVal) dec(oldVal);
        if (newVal) inc(newVal);
    }, { immediate: true })

    const forceUpdate = async () => {
        const resultId = resultIdFn();
        if (!resultId) return;
        refreshResultIds.value.add(resultId);
    }

    watchEffect(async (onInvalidate) => {
        const resultId = resultIdFn();
        if (!resultId) return;
        refreshResultIds.value.add(resultId);
        const promise = forceUpdate();
        onInvalidate(() => {
            if (resultId == resultIdFn()) return;
            refreshResultIds.value.delete(resultId);
        })
        await promise;
    })

    return { result: researchResult, refresh: forceUpdate };
}

function splitArray<T>(array: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
        array.slice(index * size, index * size + size)
    );
}

export function useResearchResultWatcher() {
    const [_inc, _dec, refCount] = useResearchResultRefCount();
    const researchResults = useResearchResultsCache();
    const update = async () => {
        const activeResultIds = Object.entries(refCount.value)
            .filter(([_, count]) => count > 0).map(([id, _]) => +id);
        const ids = activeResultIds;
        if (ids.length == 0) return;

        const groups = splitArray(ids, 100);
        const resultsByGroup = await Promise.all(groups.map(async group => {
            const res = await $fetch('/api/results', { query: { ids: group.join(',') } });
            return Object.entries(res);
        }));
        const results = resultsByGroup.flat();
        researchResults.value = Object.values(results).reduce((acc, [id, result]) => {
            acc[+id] = result;
            return acc;
        }, researchResults.value);
    }
    const regularUpdate = async () => {
        await update().catch(_ => { });
        setTimeout(regularUpdate, 5000);
    }
    regularUpdate();
}

export function useInspectResearchResult() {
    const researchResults = useResearchResultsCache();
    return (resultId: number) => {
        if (!(resultId in researchResults.value)) return undefined;
        return researchResults.value[resultId];
    }
}